<template>
  <div>
    <h1>Profile</h1>

    <div class="mt-10 bg-white rounded-lg shadow-lg p-10">
      <h2>{{ user.firstName }} {{ user.lastName }}</h2>

      <p>
        {{ user.email }} &bull; {{ user.business.phone }} &bull;
        {{ user.blocked ? "Blocked" : "Not blocked" }} &bull;
        {{
          user.activated ? "Account activated" : "Has not yet activated account"
        }}
      </p>

      <div class="mt-10 flex justify-end">
        <el-button
          size="small"
          type="primary"
          round
          @click="unlockAccount(user.id)"
          :disabled="!user.accountLocked"
          >Unlock account</el-button
        >

        <el-button
          size="small"
          type="primary"
          plain
          round
          @click="user.blocked ? unblockUser(user.id) : blockUser(user.id)"
          >{{ user.blocked ? "Unblock user" : "Block user" }}</el-button
        >
      </div>
    </div>

    <div class="mt-10" v-if="business">
      <el-tabs>
        <el-tab-pane label="Business">
          <div class="w-1/2">
            <label>Street address</label>
            <p class="mb-6">{{ business.streetAddress }}</p>

            <label>City</label>
            <p class="mb-6">{{ business.city }}</p>

            <label>Description</label>
            <p class="mb-6">{{ business.description }}</p>

            <label>Website</label>
            <p class="mb-6">
              <a :href="business.website">{{ business.website }}</a>
            </p>

            <label>Phone</label>
            <p class="mb-6">{{ business.phone }}</p>

            <label>RC number</label>
            <p class="mb-6">{{ business.rcNumber }}</p>

            <label>Year of incorporation</label>
            <p class="mb-6">{{ business.yearOfIncorporation }}</p>

            <label>Company size</label>
            <p class="mb-6">{{ business.companySize }}</p>

            <label>Customer type</label>
            <p class="mb-6">{{ business.customerType }}</p>

            <label>Industry type</label>
            <p class="mb-6">{{ business.industryType }}</p>

            <label>Entity type</label>
            <p class="mb-6">{{ business.entityType }}</p>

            <label>Desired line of credit</label>
            <p class="mb-6">{{ business.desiredLineOfCredit }}</p>

            <label>Annual revenue</label>
            <p class="mb-6">{{ business.annualRevenue }}</p>

            <label>Documents</label>
            <ul class="flex flex-wrap">
              <li
                class="mb-2 mr-4"
                v-for="document in business.businessDocuments"
                :key="document.id"
              >
                <a :href="document.upload.url" target="_blank">
                  {{ document.upload.url }}
                </a>
              </li>
            </ul>
            <ul>
              <li
                class="mb-6"
                v-for="director in business.businessDirectors"
                :key="director.id"
              >
                <h2>
                  {{ director.firstName }} {{ director.lastName }} ({{
                    director.title
                  }})
                </h2>
                <p class="mb-6">
                  {{ director.email }} &bull; {{ director.phone }} &bull;
                  {{ director.country.name }}
                </p>

                <label>BVN</label>
                <p class="mb-6">{{ director.bvn }}</p>

                <label>Date of birth</label>
                <p class="mb-6">{{ new Date(director.dob).toDateString() }}</p>

                <label>City, State</label>
                <p class="mb-6">{{ director.city }}, {{ director.state }}</p>

                <label>Identification</label>
                <p class="mb-6">
                  {{ director.identificationType }} ({{
                    director.identificationNumber
                  }})
                </p>

                <label>Documents</label>
                <ul class="flex flex-wrap">
                  <li
                    class="mb-2 mr-4"
                    v-for="document in director.businessDirectorDocuments"
                    :key="document.id"
                  >
                    <a :href="document.upload.url" target="_blank">{{
                      document.upload.url
                    }}</a>
                  </li>
                </ul>
                <hr />
              </li>
            </ul>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Account">
          <div class="mb-10 flex flex-wrap justify-end">
            <el-button
              size="small"
              @click="showUpdateCreditLimitDialog = true"
              :disabled="isUpdatingCreditLimit"
              :loading="isUpdatingCreditLimit"
              >Update credit limit</el-button
            >
          </div>
          <label>Account balance</label>
          <p class="mb-6">{{ formatMoney(business.accountBalance) }}</p>
          <label>Money in</label>
          <p class="mb-6">{{ formatMoney(business.moneyIn) }}</p>
          <label>Money out</label>
          <p class="mb-6">{{ formatMoney(business.moneyOut) }}</p>
          <label>Credit limit</label>
          <p class="mb-6">{{ formatMoney(business.swipeCredit) }}</p>
          <label>Credit spent</label>
          <p class="mb-6">{{ formatMoney(business.swipeCreditSpent) }}</p>
          <label>Interest accumulated on credit</label>
          <p class="mb-6">{{ formatMoney(business.swipeCreditInterest) }}</p>
          <label>Repayment due</label>
          <p v-if="business.repaymentDueOn">
            {{ business.repaymentDueOn | moment("from") }}
          </p>
          <p v-else>N/A</p>
        </el-tab-pane>
        <el-tab-pane label="Credit">
          <table>
            <thead>
              <th>Amount</th>
              <th>Remainder</th>
              <th>Interest</th>
              <th>Withdrawn on</th>
              <th>Repayment due</th>
            </thead>
            <tbody>
              <tr
                v-for="creditWithdrawal in business.creditWithdrawals"
                :key="creditWithdrawal.id"
              >
                <td>{{ formatMoney(creditWithdrawal.amount) }}</td>
                <td>
                  <span v-if="!creditWithdrawal.repaid">{{
                    formatMoney(creditWithdrawal.repaymentAmount)
                  }}</span>
                  <span v-else>NGN 0</span>
                </td>
                <td>{{ formatMoney(creditWithdrawal.interest) }}</td>
                <td>
                  {{
                    creditWithdrawal.createdAt | moment("dddd, MMMM Do YYYY")
                  }}
                </td>
                <td>
                  <span v-if="creditWithdrawal.repaid">
                    Repaid ({{
                      creditWithdrawal.repaidOn | moment("dddd, MMMM Do YYYY")
                    }})
                  </span>
                  <span v-else>
                    {{ creditWithdrawal.dueOn | moment("from") }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog
      title="Update credit limit"
      :visible.sync="showUpdateCreditLimitDialog"
      width="20%"
    >
      <label>Credit limit</label>
      <el-input v-model.number="creditLimit" />

      <span slot="footer">
        <el-button @click="updateCreditLimit">Update</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { USER_PROFILE_QUERY } from "@/api/queries";
import {
  BLOCK_USER_MUTATION,
  UNBLOCK_USER_MUTATION,
  UPDATE_CREDIT_LIMIT_MUTATION,
  DEBIT_REPAYMENT_MUTATION,
  UNLOCK_ACCOUNT_MUTATION
} from "@/api/mutations";

export default {
  apollo: {
    user: {
      query: USER_PROFILE_QUERY,
      update: data => data.userProfile,
      variables() {
        return { id: Number.parseInt(this.$route.params.id) };
      },
      result({ data }) {
        this.creditLimit =
          data.userProfile.business.swipeCredit /
          data.userProfile.business.currency.factor;
        this.formatter = new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: data.userProfile.business.currency.code
        });
      }
    }
  },
  computed: {
    business() {
      return this.user.business;
    },
    currency() {
      return this.business.currency;
    }
  },
  data() {
    return {
      user: { business: { country: {}, currency: {} } },
      formatter: null,
      showUpdateCreditLimitDialog: false,
      isUpdatingCreditLimit: false,
      creditLimit: 0
    };
  },
  methods: {
    async unlockAccount(id) {
      const { data } = await this.$apollo.mutate({
        mutation: UNLOCK_ACCOUNT_MUTATION,
        variables: { id }
      });
      console.log(data);

      if (data.unlockAccount.status == 200) {
        this.user.accountLocked = false;
        this.toastMessage("Account unlocked");
      }
    },
    async blockUser(id) {
      const { data } = await this.$apollo.mutate({
        mutation: BLOCK_USER_MUTATION,
        variables: { id }
      });

      if (data.blockUser.status == 200) {
        this.user.blocked = true;
        this.toastMessage("User blocked");
      }
    },
    async unblockUser(id) {
      const { data } = await this.$apollo.mutate({
        mutation: UNBLOCK_USER_MUTATION,
        variables: { id }
      });

      if (data.blockUser.status == 200) {
        this.user.blocked = false;
        this.toastMessage("User unblocked");
      }
    },
    formatMoney(amount) {
      return this.formatter.format(amount / this.currency.factor);
    },
    async updateCreditLimit() {
      this.isUpdatingCreditLimit = true;

      const { data } = await this.$apollo.mutate({
        mutation: UPDATE_CREDIT_LIMIT_MUTATION,
        variables: {
          creditLimit: this.creditLimit * this.currency.factor,
          businessId: this.business.id
        }
      });

      if (data.updateCreditLimit.status == 200) {
        this.creditLimit =
          data.updateCreditLimit.business.swipeCredit /
          this.business.currency.factor;
        this.business.swipeCredit = data.updateCreditLimit.business.swipeCredit;
        this.toastMessage("Credit limit updated");
        this.showUpdateCreditLimitDialog = false;
      }

      this.isUpdatingCreditLimit = false;
    }
  }
};
</script>
